import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DemandeSignaturePresentee,
  EnvoiSignature,
  SourceSignature,
  EtatSignature,
} from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { useMutation } from './mutation.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
const chemin = (g: string, suite = '') => `/g/${encodeURIComponent(g)}/signatures${suite}`;
export const signaturesApi = {
  lister: (
    g: string,
    filtres: {
      type?: SourceSignature['type'];
      sourceId?: string;
      etat?: EtatSignature;
      page?: number;
    },
    appel?: AppelApi,
  ) => {
    const params = new URLSearchParams(Object.entries(filtres).map(([k, v]) => [k, String(v)]));
    return api.get<{ demandes: DemandeSignaturePresentee[]; total: number }>(
      `${chemin(g)}?${params}`,
      { galerie: g, ...appel },
    );
  },
  lire: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ demande: DemandeSignaturePresentee }>(chemin(g, `/${id}`), { galerie: g, ...appel }),
  source: (g: string, source: SourceSignature, appel?: AppelApi) =>
    api.get<{ galerie: SignatairePropose[]; tiers: SignatairePropose[] }>(
      chemin(g, `/source?type=${source.type}&id=${source.id}`),
      { galerie: g, ...appel },
    ),
  envoyer: (g: string, corps: EnvoiSignature, appel?: AppelApi) =>
    api.post<{ demande: DemandeSignaturePresentee }>(chemin(g), corps, { galerie: g, ...appel }),
  geste: (
    g: string,
    id: string,
    geste: 'relancer' | 'annuler',
    corps: { revision: number; cleIdempotence: string },
    appel?: AppelApi,
  ) =>
    api.post<{ demande: DemandeSignaturePresentee }>(chemin(g, `/${id}/${geste}`), corps, {
      galerie: g,
      ...appel,
    }),
  fichier: (g: string, id: string, nature: 'signe' | 'preuve', appel?: AppelApi) =>
    api.get<{ document: { url: string } }>(chemin(g, `/${id}/${nature}`), { galerie: g, ...appel }),
};
export interface SignatairePropose {
  nom: string;
  email: string;
  telephone?: string;
}
export function useSignatures(g: string, filtres: Parameters<typeof signaturesApi.lister>[1]) {
  return useQuery({
    queryKey: cleMetier(g, 'signatures', filtres),
    queryFn: (c) => signaturesApi.lister(g, filtres, appelDeRequete(c)),
    refetchInterval: 15000,
  });
}
export function useSignature(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'signatures', 'detail', id),
    queryFn: (c) => signaturesApi.lire(g, id, appelDeRequete(c)),
    refetchInterval: 15000,
  });
}
export function useEnvoyerSignature(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: EnvoiSignature, appel) => signaturesApi.envoyer(g, corps, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(g, 'signatures') });
      },
    }),
  );
}
export function useGesteSignature(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        v: { id: string; geste: 'relancer' | 'annuler'; revision: number; cleIdempotence: string },
        appel,
      ) =>
        signaturesApi.geste(
          g,
          v.id,
          v.geste,
          { revision: v.revision, cleIdempotence: v.cleIdempotence },
          appel,
        ),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(g, 'signatures') });
      },
    }),
  );
}
